import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, Type, ViewContainerRef, reflectComponentType } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import ErrorHandling from '../shared/Utility/ErrorHandling';

/** Shown when a lazy loaded dialog cannot be created, so a broken mapping is never silent. */
export const LAZY_LOAD_FAILED_MESSAGE = 'This screen could not open. Please try again.';

function isComponentClass(value: unknown): value is Type<unknown> {
  return typeof value === 'function' && reflectComponentType(value as Type<unknown>) !== null;
}

/**
 * Safety net behind componentTypeMapping. The mapping returns the component
 * class by its named export, so normally this only confirms it is a component.
 * Given a whole module it requires exactly ONE component export.
 *
 * Never "the first export": an ES module namespace lists its exports in
 * alphabetical order, so an exported constant such as PERSON_SAVE_FAILED_MESSAGE
 * sorts ahead of PersonEntityV2Component, and createComponent() was handed a
 * string ("componentFactory.create is not a function"). That silently broke
 * Add Person Entity and Download on 2026-11-01.
 */
export function resolveComponentClass(loaded: unknown): Type<unknown> {
  if (isComponentClass(loaded)) {
    return loaded;
  }
  if (!loaded || typeof loaded !== 'object') {
    throw new Error('Lazy loaded value is not an Angular component: ' + typeof loaded);
  }
  const exportsObject = loaded as Record<string, unknown>;
  const components = Object.keys(exportsObject).filter((key) => isComponentClass(exportsObject[key]));
  if (components.length !== 1) {
    throw new Error('Lazy loaded module must export exactly one Angular component, found ' + components.length
      + ' (exports: ' + Object.keys(exportsObject).join(', ') + ')');
  }
  return exportsObject[components[0]] as Type<unknown>;
}

export enum LazyLoadableComponents {
  UIExhibitComponent,
  SketchPadComponent,
  UIClientComponent,
  NoteImagePreviewComponent,
  NotebookRenameComponent,
  LocationAndWeatherComponent,
  AiGenerateReportComponent,
  AiRecordAudioComponent,
  AiAnalyzeReportComponent,
  EditnotepopupComponent,
  MarkAsDeleteComponent,
  DeleteNotebookComponent,
  DownloadComponent,
  BookmarkComponent,
  PersonEntityV2Component,
  AdvancedComponent,
  AddQuickAttachmentComponent,
  NoteTemplatesComponent,
  UIProjectComponent,
  UIIncidentComponent,
  UIWorkRequestComponent,
  AuditComponent,
  DownloadConfirmationDialogComponent,
  LockScreenComponent,
  PreviousNoteVersionsComponent,
  SendFileRequestComponent,
  NoteSummaryComponent,

  // Other components you want to lazy load
}



@Injectable({
  providedIn: 'root'
})


export class LazyLoadService {
  private componentRefs: any[] = [];


  // Injector, not ErrorHandling/ToastrService directly: both are only needed on
  // the failure path, and resolving them lazily keeps this root service free of
  // a dependency cycle through ErrorHandling's own services.
  constructor(private injector: Injector) { }


  // ===============================================================================
  // Place ALL Components I want to dynamically create in here...
  // and in the ENUM above
  // ===============================================================================
  readonly componentTypeMapping = {
    [LazyLoadableComponents.UIExhibitComponent]: () => import('../cms/projectDisplayBlocks/ui-exhibit/ui-exhibit.component').then((m) => m.UIExhibitComponent),
    [LazyLoadableComponents.SketchPadComponent]: () => import('../sketch-pad/sketch-pad.component').then((m) => m.SketchPadComponent),
    [LazyLoadableComponents.UIClientComponent]: () => import('../cms/projectDisplayBlocks/ui-client/ui-client.component').then((m) => m.UIClientComponent),
    [LazyLoadableComponents.NoteImagePreviewComponent]: () => import('../editnote/note-image-preview/note-image-preview.component').then((m) => m.NoteImagePreviewComponent),
    [LazyLoadableComponents.NotebookRenameComponent]: () => import('../notebook-rename/notebook-rename.component').then((m) => m.NotebookRenameComponent),
    [LazyLoadableComponents.LocationAndWeatherComponent]: () => import('../locationAndWeather/locationAndWeather.component').then((m) => m.LocationAndWeatherComponent),
    [LazyLoadableComponents.AiGenerateReportComponent]: () => import('../ai/ai-generate-report/ai-generate-report.component').then((m) => m.AiGenerateReportComponent),
    [LazyLoadableComponents.AiRecordAudioComponent]: () => import('../ai/ai-record-audio/ai-record-audio.component').then((m) => m.AiRecordAudioComponent),
    [LazyLoadableComponents.AiAnalyzeReportComponent]: () => import('../ai/ai-analyze-report/ai-analyze-report.component').then((m) => m.AiAnalyzeReportComponent),
    [LazyLoadableComponents.EditnotepopupComponent]: () => import('../editnotepopup/editnotepopup.component').then((m) => m.EditnotepopupComponent),
    [LazyLoadableComponents.MarkAsDeleteComponent]: () => import('../markasdelete/markasdelete.component').then((m) => m.MarkAsDeleteComponent),
    [LazyLoadableComponents.DeleteNotebookComponent]: () => import('../delete-notebook/delete-notebook.component').then((m) => m.DeleteNotebookComponent),
    [LazyLoadableComponents.DownloadComponent]: () => import('../download/download.component').then((m) => m.DownloadComponent),
    [LazyLoadableComponents.BookmarkComponent]: () => import('../bookmark/bookmark.component').then((m) => m.BookmarkComponent),
    [LazyLoadableComponents.PersonEntityV2Component]: () => import('../components/person-entity-v2/person-entity-v2.component').then((m) => m.PersonEntityV2Component),
    [LazyLoadableComponents.AdvancedComponent]: () => import('../editnote/advanced/advanced.component').then((m) => m.AdvancedComponent),
    [LazyLoadableComponents.AddQuickAttachmentComponent]: () => import('../notebook-cards/add-quick-attachment/add-quick-attachment.component').then((m) => m.AddQuickAttachmentComponent),
    [LazyLoadableComponents.NoteTemplatesComponent]: () => import('../editnote/note-templates/note-templates.component').then((m) => m.NoteTemplatesComponent),
    [LazyLoadableComponents.UIProjectComponent]: () => import('../cms/projectDisplayBlocks/ui-project/ui-project.component').then((m) => m.UIProjectComponent),
    [LazyLoadableComponents.UIIncidentComponent]: () => import('../cms/projectDisplayBlocks/ui-incident/ui-incident.component').then((m) => m.UIIncidentComponent),
    [LazyLoadableComponents.UIWorkRequestComponent]: () => import('../cms/projectDisplayBlocks/ui-work-request/ui-work-request.component').then((m) => m.UIWorkRequestComponent),
    [LazyLoadableComponents.AuditComponent]: () => import('../editnote/audit/audit.component').then((m) => m.AuditComponent),
    [LazyLoadableComponents.DownloadConfirmationDialogComponent]: () => import('../download/download-confirmation-dialog/download-confirmation-dialog.component').then((m) => m.DownloadConfirmationDialogComponent),
    [LazyLoadableComponents.LockScreenComponent]: () => import('../lock-screen/lock-screen.component').then((m) => m.LockScreenComponent),
    [LazyLoadableComponents.PreviousNoteVersionsComponent]: () => import('../editnote/previous-note-versions/previous-note-versions.component').then((m) => m.PreviousNoteVersionsComponent),
    [LazyLoadableComponents.SendFileRequestComponent]: () => import('../editnote/send-file-request/send-file-request.component').then((m) => m.SendFileRequestComponent),
    [LazyLoadableComponents.NoteSummaryComponent]: () => import('../note-summary/note-summary.component').then((m) => m.NoteSummaryComponent),


    

    // Add other mappings for other components
  };


  // ===============================================================================
  // Gets the Component (Lazy Loaded)
  //
  // NOTE: If you have multiple variations of the same component on a page that
  // you want to show at the same time (eg: AI Analysis), then set the instanceNum
  // to different values which will allow you to create multiple versions of the
  // component.
  // ===============================================================================
  async getLazyLoadComponentInstance(componentType: LazyLoadableComponents, container: ViewContainerRef, instanceNum: number = 0) {

    console.log("LazyLoadService --> getLazyLoadComponentInstance", componentType, container);
    const importComponent = this.componentTypeMapping[componentType];
    try {
      if (!importComponent) {
        throw new Error("Unsupported component type");
      }

      const componentModule = await importComponent();
      const componentClass = resolveComponentClass(componentModule);
      const existingComponent = this.componentRefs.find(c => c.instance instanceof componentClass && c.instanceNum === instanceNum);

      if (existingComponent) {
        // --------------
        // Existing
        // --------------
        console.log("LazyLoadService --> getLazyLoadComponentInstance() --> Getting Existing Component", existingComponent.instance);
        return existingComponent.instance;
      } else {
        // --------------
        // New
        // --------------
        console.log("getLazyLoadComponentInstance() --> CREATING New Component");
        const componentRef = container.createComponent(componentClass);
        this.componentRefs.push({
          instance: componentRef.instance,
          instanceNum: instanceNum // Store the instance number
        });

        console.log("LazyLoadService --> getLazyLoadComponentInstance() --> Returning New", componentRef.instance)

        return componentRef.instance;
      }
    } catch (error) {
      this.reportFailure(componentType, error, true);
      throw error;
    }
  }


  // Toast (optional) and log a lazy load failure. The message names only the
  // component and its export names, never note or person data.
  private reportFailure(componentType: LazyLoadableComponents, error: unknown, notifyUser: boolean) {
    const name = LazyLoadableComponents[componentType] ?? String(componentType);
    if (notifyUser) {
      try {
        this.injector.get(ToastrService).error(LAZY_LOAD_FAILED_MESSAGE);
      } catch {
        // No toastr in this context; the log below still records it.
      }
    }
    try {
      // LogErrorToServer string-concatenates its error argument, so the message
      // goes in the description to reach the server log intact.
      const message = error instanceof Error ? error.message : String(error);
      this.injector.get(ErrorHandling).LogErrorToServer('LazyLoad ' + name + ': ' + message, { message } as HttpErrorResponse);
    } catch {
      console.error('LazyLoad ' + name + ' failed', error);
    }
  }

  // ===================================================================
  // Hides the specified component
  // ASSUMPTION: that the component has a hide() method
  // ===================================================================
  async hideLazyLoadComponentInstanceIfExists(componentType: LazyLoadableComponents) {
    console.log("hideLazyLoadComponentInstance", componentType);
    // const componentInstance = await this.getLazyLoadComponentInstance(componentType, null) ;

    const importComponent = this.componentTypeMapping[componentType];
    if (!importComponent) {
      throw new Error("Unsupported component type");
    }

    let componentClass: Type<unknown>;
    try {
      componentClass = resolveComponentClass(await importComponent());
    } catch (error) {
      // Nothing was asked to open, so log without a toast.
      this.reportFailure(componentType, error, false);
      throw error;
    }
    const existingComponent = this.componentRefs.find(c => c.instance instanceof componentClass);

    if (existingComponent) {
      // ------------------------
      // Hide the component
      // ------------------------
      existingComponent.instance.hide();
    }
    else {
      // -----------------------------------------
      // Not created, so no need to hide
      // -----------------------------------------
      return;
    }



  }


  // ===============================================================================
  // Be sure to add the following into the pages ngOnDestroy()
  //
  // ngOnDestroy() {
  // ...
  // this.lazyLoadService.destroy();
  // ...
  // }
  //
  // NOTE: If getting errors in console for a specific component, make sure
  // it has the destroy() method in the component.
  //
  //   destroy(){
  //    // ------------------------------------------------------
  //    // Clean up code for this component if needed.
  //    // Added to avoid error in LazyLoad destroy method
  //    // ------------------------------------------------------
  //   }
  //
  // ===============================================================================
  destroy() {
    console.log("LazyLoadService --> destroy()");
    this.componentRefs.forEach(componentRef => {
      if (componentRef && !componentRef.destroyed) {
        try {
          componentRef.instance.destroy();
        }
        catch (ex) {
          // -------------------------------------------------------------------------------------
          // NOTE: NOT using console.error() as it is expected that this WILL
          // generate exceptions as most components do not have the destroy()
          // method as not needed in most circumstances. But log left for future testing.
          // -------------------------------------------------------------------------------------
          console.log("Component Destroy() Exception = ", componentRef, ex);
        }
      }
    });
    this.componentRefs = [];
  }


}